import { useState, useRef, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { UploadCloud, X, CheckCircle2, AlertCircle, Loader2, ImagePlus } from 'lucide-react';
import { toast } from 'sonner';
import { resizeImage } from '@/lib/imageResize';
import axios from 'axios';
import { API_BASE } from '@/lib/api';

const API = API_BASE;
const MAX_PHOTOS = 30;
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif', 'image/gif'];

/**
 * Bulk photo uploader with drag-drop, multi-file preview, and a single
 * combined progress bar. Skips/flags individual failures but keeps the batch
 * going. Uses the existing single-file POST /api/upload endpoint sequentially.
 */
export default function BulkPhotoUploader({ open, onOpenChange, albumId, onComplete }) {
  const [files, setFiles] = useState([]);   // [{ id, file, status, progress, error }]
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef(null);

  const reset = useCallback(() => {
    setFiles([]);
    setUploading(false);
  }, []);

  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  const addFiles = (incoming) => {
    const list = Array.from(incoming || []);
    if (list.length === 0) return;

    const allowed = list.filter(
      (f) => f.type?.startsWith('image/') || ACCEPTED_TYPES.includes(f.type),
    );
    const rejected = list.length - allowed.length;
    if (rejected > 0) {
      toast.error(`${rejected} non-image file(s) skipped`);
    }

    setFiles((prev) => {
      const remaining = Math.max(0, MAX_PHOTOS - prev.length);
      const next = allowed.slice(0, remaining).map((f) => ({
        id: `${f.name}-${f.size}-${f.lastModified}-${Math.random()}`,
        file: f,
        status: 'queued', // queued | uploading | done | error
        progress: 0,
        error: null,
      }));
      if (allowed.length > remaining) {
        toast.error(`Max ${MAX_PHOTOS} photos per batch — ${allowed.length - remaining} skipped`);
      }
      return [...prev, ...next];
    });
  };

  const removeFile = (id) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (uploading) return;
    addFiles(e.dataTransfer?.files);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    if (!uploading) setDragging(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    setDragging(false);
  };

  const totalProgress = files.length
    ? Math.round(files.reduce((sum, f) => sum + (f.status === 'done' ? 100 : f.progress), 0) / files.length)
    : 0;
  const doneCount = files.filter((f) => f.status === 'done').length;
  const errorCount = files.filter((f) => f.status === 'error').length;

  const updateOne = (id, patch) => {
    setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, ...patch } : f)));
  };

  const startUpload = async () => {
    if (uploading || files.length === 0) return;
    setUploading(true);

    let success = 0;
    let failed = 0;

    for (const item of files) {
      if (item.status === 'done') continue;
      updateOne(item.id, { status: 'uploading', progress: 5, error: null });
      try {
        if (item.file.size === 0) {
          throw new Error('0 bytes (iCloud may still be downloading this photo)');
        }
        const resized = await resizeImage(item.file);
        const fd = new FormData();
        fd.append('file', resized, resized.name || 'photo.jpg');
        fd.append('album_id', String(albumId));
        const res = await axios.post(`${API}/upload`, fd, {
          withCredentials: true,
          onUploadProgress: (e) => {
            if (!e.total) return;
            const pct = Math.min(95, Math.round((e.loaded / e.total) * 95));
            updateOne(item.id, { progress: pct });
          },
        });
        if (!res.data?.added_to_album) {
          throw new Error('Uploaded but not linked to album');
        }
        updateOne(item.id, { status: 'done', progress: 100 });
        success++;
      } catch (err) {
        const msg = err.response?.data?.detail || err.message || 'Upload failed';
        updateOne(item.id, { status: 'error', error: msg, progress: 0 });
        failed++;
      }
    }

    setUploading(false);
    if (success > 0) {
      toast.success(`${success} photo${success === 1 ? '' : 's'} added!`);
      onComplete?.();
    }
    if (failed > 0) {
      toast.error(`${failed} photo${failed === 1 ? '' : 's'} failed — see list for details`);
    }
    if (failed === 0 && success > 0) {
      // Auto-close on full success after a beat
      setTimeout(() => onOpenChange?.(false), 800);
    }
  };

  const retryFailures = () => {
    setFiles((prev) =>
      prev.map((f) => (f.status === 'error' ? { ...f, status: 'queued', error: null, progress: 0 } : f)),
    );
    startUpload();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !uploading && onOpenChange?.(o)}>
      <DialogContent
        className="max-w-2xl rounded-3xl max-h-[90vh] overflow-hidden flex flex-col"
        data-testid="bulk-uploader-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ImagePlus className="w-5 h-5 text-teal-600" />
            Add photos to album
          </DialogTitle>
          <DialogDescription>
            Drop up to {MAX_PHOTOS} photos here, or click to browse. We&apos;ll resize and upload them in the background.
          </DialogDescription>
        </DialogHeader>

        {/* Drop zone */}
        <div
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onClick={() => !uploading && fileInputRef.current?.click()}
          role="button"
          tabIndex={0}
          className={`
            mt-2 rounded-2xl border-2 border-dashed transition-colors cursor-pointer
            flex flex-col items-center justify-center gap-2 py-8 px-4
            ${dragging ? 'border-teal-500 bg-teal-50' : 'border-stone-300 hover:border-teal-400 hover:bg-stone-50'}
            ${uploading ? 'opacity-60 cursor-not-allowed' : ''}
          `}
          data-testid="bulk-drop-zone"
        >
          <div className="w-12 h-12 rounded-2xl bg-teal-50 flex items-center justify-center">
            <UploadCloud className="w-6 h-6 text-teal-600" />
          </div>
          <p className="text-sm text-stone-700 font-medium">
            Drop photos here, or <span className="text-teal-600 underline underline-offset-2">click to browse</span>
          </p>
          <p className="text-xs text-stone-500">
            JPEG, PNG, WEBP, HEIC · up to {MAX_PHOTOS} at a time
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,.heic,.heif"
            multiple
            className="hidden"
            onChange={(e) => {
              addFiles(e.target.files);
              if (e.target) e.target.value = '';
            }}
            data-testid="bulk-file-input"
          />
        </div>

        {/* File list */}
        {files.length > 0 && (
          <div className="mt-4 flex-1 min-h-0 overflow-y-auto pr-1 space-y-2" data-testid="bulk-file-list">
            {files.map((f) => (
              <div
                key={f.id}
                className="flex items-center gap-3 p-2 rounded-xl bg-stone-50 border border-stone-100"
                data-testid={`bulk-file-${f.status}`}
              >
                <div className="w-10 h-10 rounded-lg bg-stone-200 overflow-hidden shrink-0 flex items-center justify-center">
                  <ImagePlus className="w-4 h-4 text-stone-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-stone-800 truncate">{f.file.name}</p>
                  <p className="text-xs text-stone-500">
                    {(f.file.size / 1024 / 1024).toFixed(1)} MB
                    {f.error && <span className="text-red-600"> · {f.error}</span>}
                  </p>
                  {f.status === 'uploading' && (
                    <Progress value={f.progress} className="h-1 mt-1" />
                  )}
                </div>
                <div className="shrink-0">
                  {f.status === 'done' && <CheckCircle2 className="w-5 h-5 text-teal-600" />}
                  {f.status === 'error' && <AlertCircle className="w-5 h-5 text-red-500" />}
                  {f.status === 'uploading' && <Loader2 className="w-5 h-5 text-teal-600 animate-spin" />}
                  {f.status === 'queued' && !uploading && (
                    <button
                      type="button"
                      onClick={() => removeFile(f.id)}
                      className="p-1 rounded-full hover:bg-stone-200 text-stone-500"
                      aria-label="Remove"
                      data-testid="bulk-remove-file"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        {files.length > 0 && (
          <div className="mt-4 pt-4 border-t border-stone-100 space-y-3">
            {uploading && (
              <div className="space-y-1">
                <div className="flex justify-between text-xs text-stone-600 font-medium">
                  <span>
                    {doneCount}/{files.length} uploaded
                    {errorCount > 0 && ` · ${errorCount} failed`}
                  </span>
                  <span>{totalProgress}%</span>
                </div>
                <Progress value={totalProgress} className="h-2" data-testid="bulk-combined-progress" />
              </div>
            )}
            <div className="flex gap-2 flex-wrap">
              <Button
                variant="outline"
                onClick={() => onOpenChange?.(false)}
                disabled={uploading}
                className="rounded-full"
                data-testid="bulk-cancel-btn"
              >
                {uploading ? 'Uploading…' : 'Close'}
              </Button>
              {errorCount > 0 && !uploading && (
                <Button
                  variant="outline"
                  onClick={retryFailures}
                  className="rounded-full border-amber-300 text-amber-700 hover:bg-amber-50"
                  data-testid="bulk-retry-btn"
                >
                  Retry {errorCount} failed
                </Button>
              )}
              <Button
                onClick={startUpload}
                disabled={uploading || files.every((f) => f.status === 'done')}
                className="ml-auto bg-teal-600 hover:bg-teal-700 text-white rounded-full px-6"
                data-testid="bulk-start-upload-btn"
              >
                {uploading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading…</>
                ) : (
                  <>Upload {files.filter((f) => f.status !== 'done').length} photo{files.filter((f) => f.status !== 'done').length === 1 ? '' : 's'}</>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
